import React, { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '~~elements/Button';
import Modal from '~~elements/Modal';
import Input from '~~elements/Input';
import toast from '~~elements/Toast';
import Multilingual from '~~utils/Multilingual';
import { SeasonResource } from '~~apis/resource';
import { useTournamentState, useTournamentDispatch } from '../useTournament';
import moment from 'moment';

const ModalGameDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  .score-board {
    display: flex;
    max-width: 80%;
    overflow-x: auto;
    text-align: center;
    padding: 0 0 1rem;
    .column {
      width: 2rem;
      border-right: 1px solid #eee;
      &:last-child {
        border-right: 0;
      }

      .cell {
        width: 100%;
        height: 1.6rem;
        display: block;
        border-bottom: 1px solid #eee;
        &.header {
          font-weight: 600;
        }
        &:last-child {
          border-bottom: 0;
        }
      }
    }
  }
  .basic-info {
    display: grid;
    grid-template-columns: 1fr 2fr;
    > * {
      border-bottom: 1px solid #eee;
      margin: 0;
      padding: 0.25rem 0.5rem;
      &.row-gap {
        margin-bottom: 1rem;
      }
    }
  }
`;

const FormalScheduleGameModal = ({ tournament, selectedGame, handleCancelGameSelect, modalGame }) => {
  const { organizerUniqid } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();

  const [tryDelete, setTryDelete] = useState(false);
  const [gameDeleteInput, setGameDeleteInput] = useState('');
  const [gameDeleteInProgress, setGameDeleteInProgress] = useState(false);

  useEffect(() => {
    setTryDelete(false);
  }, [modalGame]);

  const handleGameDelete = () => {
    if (!tryDelete) {
      setTryDelete(true);
      setGameDeleteInput('');
      return;
    }

    const correctInput = `G${modalGame.seq}`;
    if (gameDeleteInput !== correctInput) {
      toast(Multilingual(`ERROR.WRONG_INPUT`), { status: 'error' });
      return;
    }

    setGameDeleteInProgress(true);
    SeasonResource.deleteGame({ seasonUniqid: tournament.uniqid, gameUniqid: modalGame.uniqid })
      .then(() => {
        refetchGames().then(() => {
          toast(Multilingual(`SUCCESS.DELETE`), { status: 'success', second: 2 });
          handleCancelGameSelect();
        });
      })
      .catch((e) => {
        console.error(e);
        toast(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`), { status: 'error' });
      })
      .finally(() => {
        setGameDeleteInProgress(false);
      });
  };

  const refetchGames = () => {
    return new Promise((resolve) => {
      SeasonResource.getGames({ seasonUniqid: tournament.uniqid })
        .then(({ data: games }) => {
          tournamentDispatch({ type: 'SET_GAMES', payload: games });
          resolve();
        })
        .catch((e) => {
          window.location.reload();
        });
    });
  };

  const handleGameDeleteInput = (e) => {
    setGameDeleteInput(e.target.value);
  };

  const cancelGameDelete = () => {
    setTryDelete(false);
  };

  const getScoreBoardSection = () => {
    const scoreboardColumns = [
      ['R', '-', '-'],
      ['H', '-', '-'],
      ['E', '-', '-'],
    ];
    if (modalGame.info.status !== 'FINISHED') {
      scoreboardColumns.unshift(['-', '-', '-']);
    } else {
      scoreboardColumns[0][1] = modalGame.away.runs;
      scoreboardColumns[0][2] = modalGame.home.runs;
      scoreboardColumns[1][1] = modalGame.away.hits;
      scoreboardColumns[1][2] = modalGame.home.hits;
      scoreboardColumns[2][1] = modalGame.away.errors;
      scoreboardColumns[2][2] = modalGame.home.errors;
      for (let inning = 0; inning < modalGame.info.innings; inning++) {
        scoreboardColumns.splice(inning, 0, [
          (inning + 1).toString(),
          !modalGame.away.scores[inning] ? '-' : modalGame.away.scores[inning],
          !modalGame.home.scores[inning] ? '-' : modalGame.home.scores[inning],
        ]);
      }
    }

    return (
      <div className="score-board">
        {scoreboardColumns.map((scoreboardColumn, i) => (
          <div key={i} className="column">
            <span className="cell header">{scoreboardColumn[0]}</span>
            <span className="cell">{scoreboardColumn[1]}</span>
            <span className="cell">{scoreboardColumn[2]}</span>
          </div>
        ))}
      </div>
    );
  };

  const getFinishedGameInfo = () => {
    const finishedStatusFragment = (
      <Fragment>
        <h4>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.FINISHED_STATUS.${modalGame.info.finished_status}`)}</label>
      </Fragment>
    );

    if (modalGame.info.finished_status === 'SUSPENDED') {
      return finishedStatusFragment;
    }

    let matchResultWording = `${modalGame.away.runs}:${modalGame.home.runs} `;
    switch (modalGame.info.winner_side) {
      case 'AWAY':
        matchResultWording += `- ${modalGame.away.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'HOME':
        matchResultWording += `- ${modalGame.home.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'TIE':
        matchResultWording += Multilingual(`GAME.MATCH_RESULT.DRAW`);
        break;
      default:
        return finishedStatusFragment;
    }

    return (
      <Fragment>
        {finishedStatusFragment}
        <h4>{Multilingual(`GAME.MATCH_RESULT.WORDING`)}</h4>
        <label>{matchResultWording}</label>
      </Fragment>
    );
  };

  const getBody = () => {
    return (
      <ModalGameDiv>
        {getScoreBoardSection()}
        <div className="basic-info">
          <h4>{Multilingual(`GAME.AWAY`)}</h4>
          <label>{modalGame.away.team}</label>
          <h4 className="row-gap">{Multilingual(`GAME.HOME`)}</h4>
          <label className="row-gap">{modalGame.home.team}</label>
          <h4>{Multilingual(`GAME.STATUS.WORDING`)}</h4>
          <label>{Multilingual(`GAME.STATUS.${modalGame.info.status}`)}</label>
          {modalGame.info.status === 'FINISHED' && getFinishedGameInfo()}
          <h4>{Multilingual(`GAME.SCHEDULED_START_AT`)}</h4>
          <label>
            {moment(modalGame.info.scheduled_start_at).isValid()
              ? moment(modalGame.info.scheduled_start_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4>{Multilingual(`GAME.STARTED_AT`)}</h4>
          <label>
            {moment(modalGame.info.started_at).isValid()
              ? moment(modalGame.info.started_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4 className="row-gap">{Multilingual(`GAME.ENDED_AT`)}</h4>
          <label className="row-gap">
            {moment(modalGame.info.ended_at).isValid()
              ? moment(modalGame.info.ended_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </label>
          <h4>{Multilingual(`GAME.LOCATION`)}</h4>
          <label>{modalGame.info.location}</label>
          <h4>{Multilingual(`GAME.STATS_COMPLETENESS.WORDING`)}</h4>
          <label>{Multilingual(`GAME.STATS_COMPLETENESS.${modalGame.info.stats_completeness}`)}</label>
        </div>
      </ModalGameDiv>
    );
  };

  const getFooter = () => {
    if (tryDelete) {
      return (
        <div style={{ width: '100%', overflowX: 'auto', textAlign: 'center' }}>
          <label style={{ fontSize: '0.8rem' }}>
            {Multilingual(`TOURNAMENT.GAME_DELETE_HINT`)}
            <Input
              width="5rem"
              placeholder={`G${modalGame.seq}`}
              value={gameDeleteInput}
              onChange={handleGameDeleteInput}
            />
          </label>
          <Button width="unset" nowrap color="red" onClick={handleGameDelete} disabled={gameDeleteInProgress}>
            {Multilingual(`COMMON.DELETE_CONFIRM`)}
          </Button>
          <Button
            width="unset"
            nowrap
            color="transparentWithBorder"
            onClick={cancelGameDelete}
            disabled={gameDeleteInProgress}
          >
            {Multilingual(`COMMON.CANCEL`)}
          </Button>
        </div>
      );
    }

    return (
      <Fragment>
        <a
          href={`${process.env.REACT_APP_MAINPAGE_URL}/gamenote/preview/${tournament.uniqid}/${modalGame.uniqid}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button width="unset" color="red" nowrap>
            製作GN
          </Button>
        </a>
        <Link
          to={(location) => ({
            ...location,
            pathname: `/organizer/${organizerUniqid}/tournament/${tournament.uniqid}/game`,
            search: `gid=${modalGame.uniqid}`,
          })}
        >
          <Button width="unset" color="red" nowrap>
            {Multilingual(`COMMON.EDIT`)}
          </Button>
        </Link>
        <a
          href={`${process.env.REACT_APP_MAINPAGE_URL}/season/${tournament.uniqid}?game=${modalGame.uniqid}&tab=box`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button width="unset" color="blue" nowrap>
            {Multilingual(`COMMON.VIEW`)}
          </Button>
        </a>
        <Button width="unset" mini nowrap color="transparent" onClick={handleGameDelete}>
          {Multilingual(`COMMON.DELETE`)}
        </Button>
      </Fragment>
    );
  };

  return (
    <Modal.Modal
      size="lg"
      isOpen={!!selectedGame}
      outterClickBack={!gameDeleteInProgress}
      onClickBack={handleCancelGameSelect}
    >
      <Modal.ModalHeader hasBack={!gameDeleteInProgress} onClickBack={handleCancelGameSelect}>
        {`G${modalGame.seq}`}
      </Modal.ModalHeader>
      <Modal.ModalBody>{getBody()}</Modal.ModalBody>
      <Modal.ModalFooter>{getFooter()}</Modal.ModalFooter>
    </Modal.Modal>
  );
};

export default FormalScheduleGameModal;
